import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Spacing, Text } from '@reservamos/elements';
import LocalizedCurrency from 'components/LocalizedCurrency';
import CheckList from '../../../../ui/atoms/CheckList';
import PaymentImage from '../../../../ui/atoms/PaymentImage';
import PaycashLogos from '../../../../ui/templates/PaycashLogos';
import { formatDeadline, tArray } from '../paymentFormatUtils';

/**
 * PaycashVoucher.
 * PayCash printable store-payment voucher, ported from frontline-platform's `paycash_payment_format`
 * template. Shows the amount, the reference (and barcode when present), the payment deadline in the
 * payment timezone, the PayCash store logos, and the step instructions.
 *
 * @returns {JSX.Element} The PayCash voucher.
 */
const PaycashVoucher = () => {
  const { t } = useTranslation('payment');
  const { reference, barcodeUrl, expirationDate } = useSelector((state) => state.payment.toJS());
  const { total } = useSelector((state) => state.purchase.toJS());

  const deadline = formatDeadline(expirationDate);

  return (
    <Spacing vertical size="M">
      <PaymentImage type="paycash-logo" />
      <PaycashLogos />

      <Spacing vertical size="S" alignItems="center">
        <Text size="M" weight="semibold">
          {t('payment_format.amount_to_pay')}
        </Text>
        {/* Pinned to 2 decimals for the same reason as the OXXO voucher: this is the exact cash
            amount paid at the store, not a trip price. */}
        <LocalizedCurrency
          price={total}
          currencyStyle="currency"
          decimals={2}
          size="XL"
          weight="bold"
        />
        <Text size="S" color="grayMedium" textAlign="center">
          {t('payment_format.paycash_fee_note')}
        </Text>
      </Spacing>

      <Spacing vertical size="S" alignItems="center">
        <Text size="S" weight="semibold">
          {t('payment_format.reference')}
        </Text>
        {Boolean(barcodeUrl) && <img src={barcodeUrl} alt={t('payment_format.reference')} />}
        <Text size="XL" weight="bold" textAlign="center">
          {reference}
        </Text>
      </Spacing>

      <Spacing vertical size="S" alignItems="center">
        <Text size="S" weight="semibold">
          {t('payment_format.deadline')}
        </Text>
        <Text size="M" textAlign="center">
          {t('payment_format.before_date_hrs', { date: deadline.date, time: deadline.time })}
        </Text>
      </Spacing>

      <CheckList
        title={t('payment_format.paycash_receive_tickets_title')}
        labels={tArray(t, 'payment_format.paycash_steps')}
      />
    </Spacing>
  );
};

export default PaycashVoucher;
